import type { NotUndefined } from "@shared/frontend/hyper-table";
import { Fields, type FieldOptions } from "remult";

type AllKeysOfNested<T, Seen extends any[] = []> =
  NotUndefined<T> extends Seen[number]
    ? never
    : NotUndefined<T> extends Date
      ? never
      : NotUndefined<T> extends string | number | boolean | symbol | bigint
        ? never
        : NotUndefined<T> extends any[]
          ? NotUndefined<T>[number] extends
              | string
              | number
              | boolean
              | symbol
              | bigint
            ? never
            : AllKeysOfNested<
                NotUndefined<T>[number],
                [...Seen, NotUndefined<T>]
              >
          : NotUndefined<T> extends Record<string, any>
            ?
                | keyof NotUndefined<T>
                | AllKeysOfNested<
                    NotUndefined<T>[keyof T],
                    [...Seen, NotUndefined<T>]
                  >
            : never;
/**
 * A field of type JSON, this field type is required for json filtering
 *
 * @example
 * ```ts
 * *@JsonField<MyEntity, MyEntity["coordinates"]>({
 *  allowedKeys: [...]
 * })
 * coordinates: {....}
 * ```
 */
export function JsonField<entityType = unknown, valueType = unknown>(
  ...options: (FieldOptions<entityType, valueType> &
    ([AllKeysOfNested<valueType>] extends [never]
      ? {}
      : {
          /**
           * ### A list of allowed keys to filter this field without the fear of sql injection
           * if a key is not listed here, it will not be approved and querying this field will throw an error
           *
           * @example
           * ```ts
           * *@JsonField<MyEntity, MyEntity["coordinates"]>({
           *  allowedKeys: ["lat", "lng", "street", "house", "address"]
           * })
           * coordinates?: {lat: number, lng: number, address: {
           *  street: string,
           *  house: number,
           * }}
           * ```
           */
          allowedKeys: AllKeysOfNested<valueType>[];
        }))[]
) {
  return Fields.json<entityType, valueType>(
    {
      allowedKeys: [],
    } as any,
    ...options,
  );
}
